import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import axios from "axios";

import type { Document, SignedDocument } from "@/types/api/document";

import api from "./api";

interface UploadDocumentInput {
  title: string;
  isPeriotools: boolean;
  patientId: number;
  file: File;
}

export function useUploadDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      title,
      isPeriotools,
      patientId,
      file,
    }: UploadDocumentInput) => {
      // 1. Request signed URL from backend
      const { data: signed } = await api.get("/s3/sign-upload", {
        params: {
          filename: file.name,
          mimetype: file.type,
          patientId,
        },
      });

      const { url, baseLink } = signed;

      // 2. Upload the file to S3
      await axios.put(url, file, {
        headers: {
          "Content-Type": file.type,
        },
      });

      // 3. Create document record in DB
      const isPdf = file.type === "application/pdf";

      const { data: document } = await api.post<Document>("/documents", {
        title,
        s3BaseLink: baseLink,
        isPdf,
        isPeriotools,
        patientId,
      });

      return document;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["documents"],
      });
    },
  });
}

const getPatientDocuments = async (
  patientId: string,
  isPeriotools: boolean
) => {
  return await api
    .get<Array<SignedDocument>>(`/documents/${patientId}`, {
      params: { isPeriotools },
    })
    .then((res) => res.data);
};

export const usePatientDocuments = (
  patientId: string,
  isPeriotools: boolean
) => {
  return useQuery({
    queryKey: ["documents", patientId, isPeriotools],
    queryFn: () => getPatientDocuments(patientId, isPeriotools),
    enabled: !!patientId,
    placeholderData: keepPreviousData,
    staleTime: 3000,
  });
};

const deleteDocument = async (documentId: string) => {
  return await api.delete(`/documents/${documentId}`).then((res) => res.data);
};

export const useDeleteDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteDocument,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
  });
};
